import { VariantDetail } from "../../../../Types";

export const FIELD_VALIDATORS: Record<
  string,
  (v: any, all: any, additionalParams?: any) => string[]
> = {
  featureKey: (v, all, additionalParams) => {
    const { enableExtensionPrefix } = all;
    const { extensionPrefix = "" } = additionalParams || {};
    const errors: string[] = [];
    if (!v) errors.push("Required");
    if (typeof v === "string" && /\s$/.test(v))
      errors.push("No trailing whitespace allowed");
    if (v && !/^(?!d-|-)[a-z0-9][a-z0-9-]*$/.test(v))
      errors.push("Invalid format");
    if (v && enableExtensionPrefix && `${extensionPrefix}${v}`.length > 54) {
      errors.push("Max 54 chars (including prefix)");
    }
    if (v && !enableExtensionPrefix && v.length > 54) {
      errors.push("Max 54 chars");
    }
    return errors;
  },
  featureName: (v) => {
    const errors: string[] = [];
    if (!v) errors.push("Required");
    if (typeof v === "string" && /\s$/.test(v))
      errors.push("No trailing whitespace allowed");
    return errors;
  },
  featureDescription: (v) => {
    const errors: string[] = [];
    if (!v) errors.push("Required");
    if (typeof v === "string" && /\s$/.test(v))
      errors.push("No trailing whitespace allowed");
    return errors;
  },
  extensionFilters: (v, _, additionalParams) => {
    const errors: string[] = [];
    const { extensionConfig } = additionalParams || {};
    // Skip required check since extension filters are already set in base config
    if (
      extensionConfig?.extensionFilters &&
      extensionConfig?.extensionFilters.length > 0
    ) {
      return errors;
    }
    if (!v || !Array.isArray(v) || v.length === 0) {
      errors.push("At least one extension filter is required");
    } else {
      // Check each filter for validity
      const invalidFilters = v.filter(
        (filter) =>
          !filter || typeof filter !== "string" || filter.trim().length === 0,
      );
      if (invalidFilters.length > 0) {
        errors.push("All extension filters must be non-empty strings");
      }
    }
    return errors;
  },
  variantDetails: (v: VariantDetail[]) => {
    const errors: string[] = [];
    // variant details is not required
    if (!v || v.length === 0) {
      return [];
    }
    if (v.length < 2) {
      errors.push("At least 2 variants are required (1 control + 1 treatment)");
      return errors;
    }
    const controls = v.filter(
      (variant: VariantDetail) => variant.isControl === true,
    );
    const treatments = v.filter(
      (variant: VariantDetail) => variant.isControl === false,
    );
    if (controls.length !== 1) {
      errors.push("Exactly 1 control variant is required");
    }
    if (treatments.length < 1) {
      errors.push("At least 1 treatment variant is required");
    }
    // Unique names
    const names = v.map((variant: VariantDetail) => variant.name);
    const nameSet = new Set();
    for (const name of names) {
      if (nameSet.has(name)) {
        errors.push("Variant names must be unique");
        break;
      }
      nameSet.add(name);
    }
    // Name regex
    for (const name of names) {
      const nameErrors = variantDetailNameValidator(name);
      if (nameErrors.length > 0) {
        errors.push(...nameErrors);
        break;
      }
    }
    // Feature flags validation
    for (const variant of v) {
      for (const [flagKey, flagValue] of Object.entries(variant.featureFlags)) {
        if (
          typeof flagValue !== "string" &&
          typeof flagValue !== "boolean" &&
          typeof flagValue !== "number"
        ) {
          errors.push(
            `Variant '${variant.name}': Feature flag '${flagKey}' must be a string, boolean, or number`,
          );
        }
      }
    }
    return errors;
  },
  extensionVersionFiltersVersion: (v, _all) => {
    const errors: string[] = [];
    if (v && typeof v === "string") {
      if (!/^\d+(\.\d+)*(-.*)?$/.test(v.trim())) {
        errors.push("Invalid version format (expected: major.minor.patch)");
      }
      if (/\s$/.test(v)) {
        errors.push("No trailing whitespace allowed");
      }
    }
    return errors;
  },
};

export const variantDetailNameValidator = (
  name: string,
  additionalParams?: {
    enableExtensionPrefix: boolean;
    extensionPrefix: string;
  },
) => {
  const { enableExtensionPrefix, extensionPrefix } = additionalParams || {};
  const errors: string[] = [];
  const nameRegex = /^(?!d-|-)[a-z0-9][a-z0-9-]*$/;
  if (!name) errors.push("Required");
  if (!nameRegex.test(name)) {
    errors.push(
      "Variant names must be lowercase, numbers, hyphens, and cannot start with 'd-' or '-'",
    );
  }
  if (name && enableExtensionPrefix && extensionPrefix) {
    const fullName = `${extensionPrefix}${name}`;
    if (fullName.length > 64) {
      errors.push("Max 64 chars (including prefix)");
    }
  }
  if (name && !enableExtensionPrefix && name.length > 64) {
    errors.push("Max 64 chars");
  }
  return errors;
};
